import type { FieldAttributes, FormikErrors, FormikTouched, FormikValues } from 'formik'
import { Field } from 'formik'
import styles from './Input.module.scss'
import { useMemo } from 'react'
import { get } from 'lodash'
import classNames from 'classnames'

type InputProps = FieldAttributes<unknown> & {
  label: string
  errors?: FormikErrors<FormikValues>
  touched?: FormikTouched<FormikValues>
  fieldClassName?: string
  width?: number // in percentage
  textarea?: boolean
  rows?: number
}

export const Input = ({
  label,
  errors,
  touched,
  name,
  required,
  fieldClassName,
  width = 100,
  textarea = false,
  rows = 4,
  ...rest
}: InputProps) => {
  const { hasError } = useMemo(() => {
    return {
      hasError: get(errors, name) && get(touched, name),
    }
  }, [errors, touched, name])

  const inputClasses = classNames(textarea ? styles.textarea : styles.input, {
    [styles.error]: hasError,
  })
  const inputId = `${name}`
  const errorId = `${name}-error`

  const calculatedWidth = useMemo(() => {
    const gap = 18 / 2
    if (width !== 100) return `calc(${width}% - ${gap}px)`

    return '100%'
  }, [width])

  return (
    <div
      className={classNames(styles.field, fieldClassName)}
      style={{
        width: `${calculatedWidth}`,
        flex: `0 0 ${calculatedWidth}`,
      }}
    >
      <div className={styles.fieldWrapper}>
        <label htmlFor={inputId} className={styles.label}>
          {label}
          {required ? <span className={styles.required}> *</span> : null}
        </label>
        <Field
          id={inputId}
          name={name}
          required={required}
          as={textarea ? 'textarea' : undefined}
          rows={textarea ? rows : undefined}
          {...rest}
          className={inputClasses}
          data-error={hasError}
          aria-required={Boolean(required)}
          aria-invalid={Boolean(hasError)}
          aria-describedby={hasError ? errorId : undefined}
        />
      </div>
      {hasError ? (
        <div id={errorId} className={styles.error} role="alert">
          {String(get(errors, name))}
        </div>
      ) : null}
    </div>
  )
}
